import { ArrowSquareOut, ArrowsClockwise, Certificate, LinkBreak } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { type LicenseStatus, licenseQuery } from "@/components/admin/api";
import { QueryBoundary } from "@/components/admin/shared";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Badge,
  type BadgeTone,
  Button,
  buttonStyles,
  Callout,
  ConfirmDialog,
  RelativeTime,
  RowSkeleton,
  SectionHeader,
} from "@/components/ui";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_app/admin/license")({ component: LicensePage });

const STATE: Record<LicenseStatus["state"], { label: () => string; tone: BadgeTone }> = {
  none: { label: () => m.adm_lic_none(), tone: "neutral" },
  active: { label: () => m.adm_lic_active(), tone: "success" },
  grace: { label: () => m.adm_lic_grace(), tone: "warning" },
  expired: { label: () => m.adm_lic_expired(), tone: "danger" },
};

/** Links this installation to a Cyrellian account (device flow: code, approve page, the page polls until confirmed). */
function LicensePage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_license() }]);
  const license = useQuery(licenseQuery);
  const qc = useQueryClient();
  const post = (path: string) => () => api.post<{ license: LicenseStatus }>(`/api/v1/admin/license${path}`);
  const onSuccess = (data: { license: LicenseStatus }) => qc.setQueryData(licenseQuery.queryKey, data);
  const link = useMutation({ mutationFn: post("/link"), onSuccess });
  const poll = useMutation({ mutationFn: post("/poll"), onSuccess });
  const cancel = useMutation({ mutationFn: post("/cancel"), onSuccess });
  const refresh = useMutation({ mutationFn: post("/refresh"), onSuccess });
  const unlink = useMutation({ mutationFn: post("/unlink"), onSuccess });
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  // While a code waits for confirmation, ask every few seconds (Cyrellian allows 60 polls a minute).
  const pending = !!license.data?.license.pending;
  const pollNow = poll.mutate;
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => pollNow(), 4000);
    return () => clearInterval(t);
  }, [pending, pollNow]);

  const failure = link.error ?? refresh.error ?? unlink.error;

  return (
    <Page title={m.adm_nav_license()} icon={Certificate} meta={m.adm_lic_meta()} width="full" className="max-w-3xl">
      <QueryBoundary query={license} skeleton={<RowSkeleton />} needs="instance.admin">
        {({ license: l }) => (
          <div className="flex flex-col gap-6">
            {!l.enabled ? (
              <Callout tone="info" title={m.adm_lic_off_title()}>
                {m.adm_lic_off_body()}
              </Callout>
            ) : null}
            {failure ? <Callout tone="danger">{failure.message}</Callout> : null}

            <section aria-labelledby="lic-status" className="flex flex-col gap-4">
              <SectionHeader id="lic-status" title={m.adm_lic_status_title()} />
              <div className="flex flex-col gap-4 rounded-panel border border-line bg-surface p-5 shadow-[0_1px_2px_var(--shadow-soft)]">
                <div>
                  <Badge tone={STATE[l.state].tone}>{STATE[l.state].label()}</Badge>
                </div>
                {l.state !== "none" ? (
                  <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
                    <dt className="text-fg-muted">{m.adm_lic_account()}</dt>
                    <dd className="text-fg">{l.account}</dd>
                    <dt className="text-fg-muted">{m.adm_lic_plan()}</dt>
                    <dd className="text-fg">{l.planName}</dd>
                    <dt className="text-fg-muted">{m.adm_lic_valid_until()}</dt>
                    <dd className="text-fg">{l.expiresAt ? <RelativeTime value={l.expiresAt} /> : null}</dd>
                    <dt className="text-fg-muted">{m.adm_lic_refreshed()}</dt>
                    <dd className="text-fg">{l.refreshedAt ? <RelativeTime value={l.refreshedAt} /> : null}</dd>
                    <dt className="text-fg-muted">{m.adm_lic_instance()}</dt>
                    <dd className="font-mono text-xs text-fg">{l.instanceId}</dd>
                  </dl>
                ) : null}
                {l.state === "grace" ? <Callout tone="warning">{m.adm_lic_grace_body()}</Callout> : null}
                {l.error ? <Callout tone="warning">{m.adm_lic_error({ error: l.error })}</Callout> : null}

                {l.enabled && l.state !== "none" ? (
                  <div className="flex flex-wrap gap-2">
                    <Button onPress={() => refresh.mutate()} isPending={refresh.isPending}>
                      <ArrowsClockwise aria-hidden size={16} />
                      {m.adm_lic_refresh()}
                    </Button>
                    <Button variant="danger" onPress={() => setConfirmUnlink(true)}>
                      <LinkBreak aria-hidden size={16} />
                      {m.adm_lic_unlink()}
                    </Button>
                  </div>
                ) : null}
              </div>
            </section>

            {l.enabled && l.state === "none" && !l.pending ? (
              <section aria-labelledby="lic-link" className="flex flex-col gap-3">
                <SectionHeader id="lic-link" title={m.adm_lic_link()} description={m.adm_lic_link_body()} />
                <div>
                  <Button variant="primary" onPress={() => link.mutate()} isPending={link.isPending}>
                    {m.adm_lic_link()}
                  </Button>
                </div>
              </section>
            ) : null}

            {l.pending ? (
              <Callout
                tone="info"
                title={m.adm_lic_pending_title()}
                actions={
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={l.pending.approveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={buttonStyles({ variant: "primary" })}
                    >
                      <ArrowSquareOut aria-hidden size={16} />
                      {m.adm_lic_open()}
                    </a>
                    <Button onPress={() => cancel.mutate()} isPending={cancel.isPending}>
                      {m.adm_lic_cancel()}
                    </Button>
                  </div>
                }
              >
                {m.adm_lic_pending_body({
                  code: l.pending.code,
                  time: new Date(l.pending.expiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                })}
              </Callout>
            ) : null}
          </div>
        )}
      </QueryBoundary>
      <ConfirmDialog
        isOpen={confirmUnlink}
        onOpenChange={setConfirmUnlink}
        title={m.adm_lic_unlink_title()}
        description={m.adm_lic_unlink_body()}
        confirmLabel={m.adm_lic_unlink()}
        tone="danger"
        onConfirm={() => unlink.mutateAsync()}
      />
    </Page>
  );
}
